import { SelectOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import { FC, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TaskRoute } from "tm-shared/types";

import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import BaseButton from "@/shared/ui/BaseButton";
import { BaseModal } from "@/shared/ui/BaseModal";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { selectUncompletedTasks } from "@/store/slices/progress";
import { clearReminderModals } from "@/store/slices/ui";

import { StyledTaskCard } from "./Styled";

export interface UncompletedTaskModalProps {
    onCloseModal: () => void;
}

export const UncompletedTaskModal: FC<UncompletedTaskModalProps> = ({
    onCloseModal,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const uncompletedTasks = useAppSelector(selectUncompletedTasks);
    const points = useAppSelector((s) => s.work.points);

    const handleNavigateToTask = ({
        pointId,
        taskId,
        taskRoute,
    }: {
        taskId: string;
        pointId: string;
        taskRoute: TaskRoute;
    }) => {
        navigate(`${ROUTES.WorkPlan}/${pointId}/${taskRoute}/${taskId}`);

        dispatch(clearReminderModals());
    };

    const handleClose = useCallback(() => {
        onCloseModal();
    }, [onCloseModal]);

    const handleIgnore = () => {
        onCloseModal();
    };

    useEffect(() => {
        if (uncompletedTasks.length === 0) {
            handleClose();
        }
    }, [uncompletedTasks.length, handleClose]);

    if (uncompletedTasks.length === 0) return null;

    return (
        <BaseModal
            open
            onClose={handleClose}
            actions={null}
            mask={{ closable: false }}
            title={"У вас є незавершені задачі"}
        >
            <Flex vertical gap={16}>
                <Flex vertical gap={12}>
                    {uncompletedTasks.map(
                        ({ id, targetId, name, context: { route } }) => {
                            const pointName =
                                points[targetId]?.name ?? "Торгова точка";

                            return (
                                <StyledTaskCard
                                    key={id}
                                    align="center"
                                    justify="space-between"
                                    gap={8}
                                >
                                    <Flex vertical gap={4}>
                                        <PrimaryText fontWeight="500">
                                            {name}
                                        </PrimaryText>
                                        <SecondaryText fontSize={13}>
                                            {pointName}
                                        </SecondaryText>
                                    </Flex>

                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<SelectOutlined />}
                                        onClick={() =>
                                            handleNavigateToTask({
                                                taskId: id,
                                                pointId: targetId,
                                                taskRoute: route,
                                            })
                                        }
                                        style={{ flexShrink: 0 }}
                                    />
                                </StyledTaskCard>
                            );
                        },
                    )}
                </Flex>

                <BaseButton type="default" onClick={handleIgnore}>
                    Ігнорувати
                </BaseButton>
            </Flex>
        </BaseModal>
    );
};
